import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { getCookie } from "../script/cookies";
import { toast } from "react-toastify";
import PageLoader from "./PageLoader";
import {
  fetchCurrentUserInfo,
  fetchFullProfile,
  fetchTransactions,
  countTransactions,
  createTransaction,
  countUserNetwork,
  fetchDirectStatusCounts,
  countNetworkByStatus,
  isAdminUser,
  fetchRankHistory,
  fetchPostIncentives,
  changeUserPassword,
  MLM_FIELDS,
  rpc,
  DB,
} from "../script/rpc";
import Pagination from "./Pagination";

/* ─────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────── */
const fmt = (val) =>
  val == null || val === false
    ? "—"
    : new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);

const fmtNum = (val) =>
  val == null ? "—" : new Intl.NumberFormat("en-IN").format(val);

function initials(name = "") {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

const formatDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v + " UTC");
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Detect MIME from magic bytes
function detectOfficeFromZip(bytes) {
  try {
    let str = "";
    const limit = Math.min(bytes.length, 8192);
    for (let i = 0; i < limit; i++) str += String.fromCharCode(bytes[i]);
    if (str.includes("word/"))
      return {
        mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ext: "docx",
      };
    if (str.includes("xl/"))
      return {
        mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ext: "xlsx",
      };
    if (str.includes("ppt/"))
      return {
        mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        ext: "pptx",
      };
  } catch (_) { }
  return { mime: "application/zip", ext: "zip" };
}
function detectMime(bytes) {
  const b = bytes;
  if (b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46)
    return { mime: "application/pdf", ext: "pdf" };
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47)
    return { mime: "image/png", ext: "png" };
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff)
    return { mime: "image/jpeg", ext: "jpg" };
  if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46)
    return { mime: "image/gif", ext: "gif" };
  if (b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50)
    return { mime: "image/webp", ext: "webp" };
  if (b[0] === 0xd0 && b[1] === 0xcf && b[2] === 0x11 && b[3] === 0xe0)
    return { mime: "application/msword", ext: "doc" };
  if (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70)
    return { mime: "video/mp4", ext: "mp4" };
  if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33)
    return { mime: "audio/mpeg", ext: "mp3" };
  if (b[0] === 0x50 && b[1] === 0x4b && b[2] === 0x03 && b[3] === 0x04)
    return detectOfficeFromZip(b);
  return { mime: "application/octet-stream", ext: "bin" };
}

/* ─────────────────────────────────────────────────────────────
   ICONS
───────────────────────────────────────────────────────────── */
const Icons = {
  dashboard: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  tree: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="4" r="2" /><circle cx="5" cy="19" r="2" /><circle cx="19" cy="19" r="2" />
      <path d="M12 6v6M12 12l-5.5 5M12 12l5.5 5" />
    </svg>
  ),
  transfer: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
    </svg>
  ),
  user: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  logout: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  rupee: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h12M6 8h12M15 21l-9-13h4a4 4 0 0 0 0-8" />
    </svg>
  ),
  globe: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 3c-4 3-5 9-5 9s1 6 5 9c4-3 5-9 5-9s-1-6-5-9z" />
      <path d="M3 12h18" />
    </svg>
  ),
  wallet: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
      <circle cx="18" cy="15" r="1" fill="currentColor" />
    </svg>
  ),
  users: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  check: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  clock: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 15" />
    </svg>
  ),
  arrowRight: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  edit: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  plus: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  menu: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  ),
  close: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  download: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  back: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  attach: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  ),
};

/* ─────────────────────────────────────────────────────────────
   COLOR MAP
───────────────────────────────────────────────────────────── */
const COLOR_MAP = {
  emerald: { bg: "#ecfdf5", icon: "#059669", bar: "#10b981", text: "#065f46" },
  blue: { bg: "#eff6ff", icon: "#2563eb", bar: "#3b82f6", text: "#1e40af" },
  amber: { bg: "#fffbeb", icon: "#d97706", bar: "#f59e0b", text: "#92400e" },
  rose: { bg: "#fff1f2", icon: "#e11d48", bar: "#f43f5e", text: "#9f1239" },
  violet: { bg: "#f5f3ff", icon: "#7c3aed", bar: "#8b5cf6", text: "#4c1d95" },
  slate: { bg: "#f8fafc", icon: "#475569", bar: "#64748b", text: "#1e293b" },
};

/* ─────────────────────────────────────────────────────────────
   MINI SPINNER
───────────────────────────────────────────────────────────── */
function MiniSpinner() {
  return (
    <span style={{
      display: "inline-block", width: 13, height: 13,
      border: "2px solid currentColor", borderTopColor: "transparent",
      borderRadius: "50%", animation: "dp-spin 0.7s linear infinite",
      verticalAlign: "middle",
    }} />
  );
}

/* ─────────────────────────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────────────────────────── */
function StateBadge({ state }) {
  const isApproved = state === "approved";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      whiteSpace: "nowrap",
      background: isApproved ? "#ecfdf5" : "#fef9c3",
      color: isApproved ? "#059669" : "#a16207",
      border: `1px solid ${isApproved ? "#a7f3d0" : "#fde68a"}`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
      {isApproved ? "Approved" : "Pending"}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────
   TRANSACTION FORM
───────────────────────────────────────────────────────────── */
function TransactionForm({ uid, password, partnerId, partnerName, nodeId, ltx, onSuccess, onDiscard }) {
  const [formData, setFormData] = useState({ amount: "", meta: "", attachment: "", attachmentName: "", state: "pending" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) =>
      setFormData((prev) => ({
        ...prev,
        attachment: evt.target.result.split(",")[1],
        attachmentName: file.name,
      }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!partnerId) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const today = new Date().toISOString().split("T")[0];
    const data = {
      partner_id: partnerId,
      node_id: nodeId,
      trans_date: today,
      transaction_number: formData.transaction_number,
      amount: parseFloat(formData.amount) || 0,
      ltx: ltx || "",
      state: formData.state,
    };
    if (formData.meta) data.meta = formData.meta;
    if (formData.attachment) data.attachment = formData.attachment;
    try {
      await createTransaction(uid, password, data);
      setSuccess("Transaction saved successfully!");
      setTimeout(() => { onSuccess(); }, 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const todayLabel = new Date().toLocaleString("en-IN", {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });

  return (
    <div className="dp-card dp-card--anim" style={{ animationDelay: "0ms", maxWidth: 900 }}>
      {/* Header */}
      <div className="dp-card-head" style={{ marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div className="dp-card-title-group">
          <div className="dp-card-icon-wrap dp-card-icon-wrap--amber">{Icons.transfer}</div>
          <div>
            <h2 className="dp-card-title">New Transaction</h2>
            <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>Fill in the details to record a new transaction</p>
          </div>
        </div>
        <button
          onClick={onDiscard}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          {Icons.back} Back to List
        </button>
      </div>

      {/* Alerts */}
      {error && <div className="dp-error-bar" style={{ marginBottom: 16 }}>⚠ {error}</div>}
      {success && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#f0fdf4", color: "#166534", borderRadius: 10, fontSize: 13, fontWeight: 500, marginBottom: 16, border: "1px solid #bbf7d0" }}>
          ✓ {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="dp-form-grid">
          {/* Amount */}
          <div>
            <label className="dp-form-label">Amount <span style={{ color: "#e11d48" }}>*</span></label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#7c3aed", fontWeight: 800, fontSize: 14 }}>₹</span>
              <input
                className="dp-form-input" type="number" step="0.01" min="0"
                name="amount" required value={formData.amount} onChange={handleChange}
                style={{ paddingLeft: 30 }} placeholder="0.00"
              />
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="dp-form-label">Transaction Date</label>
            <input className="dp-form-input" type="text" value={todayLabel} disabled />
          </div>

          {/* LTX & Attachment */}
          <div>
            <label className="dp-form-label">LTX Number</label>
            <input
              className="dp-form-input" type="text" value={(ltx || "—").replace(/\s+/g, "")} disabled
              style={{ background: "#f8fafc", fontWeight: "700", color: "#6366f1", border: "1.5px solid #6366f130" }}
            />
          </div>
          <div>
            <label className="dp-form-label">Attachment</label>
            <div className={`dp-file-zone${formData.attachment ? " dp-file-zone--ok" : ""}`} style={{ minHeight: 42, padding: "8px 14px" }}>
              <span style={{ color: formData.attachment ? "#059669" : "#94a3b8", display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
                {Icons.attach}
                {formData.attachmentName
                  ? <span style={{ fontWeight: 600, color: "#059669", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>✓ {formData.attachmentName}</span>
                  : <span style={{ fontSize: 13 }}>Choose a file…</span>}
              </span>
              <input type="file" onChange={handleFile} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
            </div>
          </div>

          {/* Notes — full width */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="dp-form-label">UTR/REF NO.</label>
            <textarea
              className="dp-form-input" name="meta" value={formData.meta}
              onChange={handleChange} rows={3}
              placeholder="Add UTR/REF NO.…"
              style={{ resize: "vertical" }}
            />
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 22, paddingTop: 18, borderTop: "1px solid #f1f5f9", flexWrap: "wrap" }}>
          <button
            type="submit" disabled={submitting}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 22px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#7c3aed,#4f46e5)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.8 : 1, boxShadow: "0 2px 8px rgba(124,58,237,0.3)" }}
          >
            {submitting ? <><MiniSpinner /> Saving…</> : <>{Icons.check} Save Transaction</>}
          </button>
          <button
            type="button" onClick={onDiscard}
            style={{ padding: "9px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            Discard
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   TRANSACTION LIST
───────────────────────────────────────────────────────────── */
function TransactionList({ transactions, totalItems, currentPage, onPageChange, onNewClick, search, onSearchChange, loading, isAdmin }) {
  const handleDownload = (t) => {
    if (!t.attachment) return;
    try {
      const byteChars = atob(t.attachment);
      const byteArr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
      const detected = detectMime(byteArr);
      const blob = new Blob([byteArr], { type: detected.mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transaction_${t.id || t.trans_date}.${detected.ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  return (
    <div className="dp-card dp-card--anim" style={{ animationDelay: "0ms", border: "0.5px solid #e2e8f0", borderRadius: 20, position: "relative", overflow: "hidden" }}>
      {/* Loading overlay */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(255,255,255,0.6)", backdropFilter: "blur(2px)",
        opacity: loading ? 1 : 0, pointerEvents: loading ? "auto" : "none",
        transition: "opacity 0.2s ease",
      }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid #e2e8f0", borderTopColor: "#7c3aed", animation: "dp-spin 0.8s linear infinite" }} />
      </div>

      {/* Header */}
      <div className="dp-card-head" style={{ marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div className="dp-card-title-group">
          <div className="dp-card-icon-wrap dp-card-icon-wrap--amber" style={{ width: 40, height: 40, borderRadius: 12 }}>{Icons.transfer}</div>
          <div>
            <h2 className="dp-card-title" style={{ fontSize: 18, marginBottom: 2 }}>Transactions History</h2>
            <p style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>{totalItems} total records</p>
          </div>
        </div>
        <button
          onClick={onNewClick}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#7c3aed,#4f46e5)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 12px rgba(124,58,237,0.25)", whiteSpace: "nowrap" }}
        >
          {Icons.plus} Add New
        </button>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 20 }}>
        <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <input
          type="text"
          placeholder="Search by amount, LTX, or description..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ width: "100%", padding: "12px 16px 12px 42px", borderRadius: 12, border: "1px solid #f1f5f9", background: "#f8fafc", fontSize: 14, fontWeight: 500, outline: "none", transition: "all 0.2s", boxSizing: "border-box" }}
        />
      </div>

      <div style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }}>
        {transactions.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 24px", color: "#94a3b8" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>No transactions yet.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="dp-tx-table-wrap">
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", tableLayout: "fixed", minWidth: 900 }}>
                <colgroup>
                  <col style={{ width: "160px" }} />
                  {isAdmin && <col style={{ width: "150px" }} />}
                  <col style={{ width: "130px" }} />
                  <col style={{ width: "130px" }} />
                  <col style={{ width: "100px" }} />
                  <col style={{ width: "110px" }} />
                  <col style={{ width: "100px" }} />
                  <col style={{ width: "160px" }} />
                  <col style={{ width: "100px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", background: "#fafafa", borderBottom: "1px solid #f1f5f9" }}>
                      Date
                    </th>
                    {isAdmin && (
                      <th style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", background: "#fafafa", borderBottom: "1px solid #f1f5f9" }}>
                        User
                      </th>
                    )}
                    {["Transaction No", "Post", "LTX", "Amount", "Attachment", "UTR/REF NO.", "Status"].map((h, i) => (
                      <th key={h} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", textAlign: i === 3 ? "right" : i === 4 ? "center" : "left", background: "#fafafa", borderBottom: "1px solid #f1f5f9" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t, idx) => (
                    <tr key={t.id} style={{ borderBottom: idx < transactions.length - 1 ? "1px solid #f8fafc" : "none", transition: "background .12s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                    >
                      <td style={{ padding: "14px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 30, height: 30, borderRadius: 8, background: "#f1f5f9", color: "#64748b", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>{formatDateTime(t.create_date || t.trans_date)}</span>
                        </div>
                      </td>
                      {isAdmin && (
                        <td style={{ padding: "14px 12px" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{t.partner_id?.[1] || "—"}</div>
                        </td>
                      )}
                      <td style={{ padding: "14px 12px" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{t.transaction_number || <span style={{ color: "#cbd5e1", fontWeight: 400 }}>—</span>}</div>
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#475569", background: "#f1f5f9", padding: "3px 8px", borderRadius: 6 }}>{t.post_id?.[1] || <span style={{ color: "#cbd5e1" }}>—</span>}</span>
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        <span style={{ fontWeight: 700, color: "#6366f1", background: "#f5f3ff", padding: "3px 8px", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }}>{t.ltx || "—"}</span>
                      </td>
                      <td style={{ padding: "14px 12px", textAlign: "right" }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>₹{Number(t.amount || 0).toLocaleString("en-IN")}</div>
                      </td>
                      <td style={{ padding: "14px 12px", textAlign: "center" }}>
                        {t.attachment ? (
                          <button onClick={() => handleDownload(t)} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#ede9fe", color: "#7c3aed", border: "none", padding: "5px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                            {Icons.download} View
                          </button>
                        ) : <span style={{ color: "#e2e8f0", fontSize: 13 }}>—</span>}
                      </td>
                      <td style={{ padding: "14px 12px", fontSize: 12, color: "#64748b", fontWeight: 500 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {t.meta || <span style={{ color: "#cbd5e1" }}>—</span>}
                        </div>
                      </td>
                      <td style={{ padding: "14px 12px", textAlign: "center" }}>
                        <StateBadge state={t.state || "pending"} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="dp-tx-card-list">
              {transactions.map((t) => (
                <div key={t.id} style={{ border: "1px solid #f1f5f9", borderRadius: 12, padding: "14px", background: "#fafafa", marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>₹{Number(t.amount || 0).toLocaleString("en-IN")}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{formatDateTime(t.create_date || t.trans_date)}</div>
                    </div>
                    <StateBadge state={t.state || "pending"} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px" }}>
                    {t.ltx && (
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>LTX</div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", background: "#f5f3ff", padding: "2px 6px", borderRadius: 5, fontFamily: "monospace" }}>{t.ltx}</span>
                      </div>
                    )}
                    {t.transaction_number && (
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Txn No.</div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>{t.transaction_number}</div>
                      </div>
                    )}
                    {t.post_id?.[1] && (
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Post</div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>{t.post_id[1]}</div>
                      </div>
                    )}
                    {t.meta && (
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>UTR/REF NO.</div>
                        <div style={{ fontSize: 11, color: "#64748b", wordBreak: "break-all" }}>{t.meta}</div>
                      </div>
                    )}
                  </div>
                  {t.attachment && (
                    <button onClick={() => handleDownload(t)} style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 5, background: "#ede9fe", color: "#7c3aed", border: "none", padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      {Icons.download} Download Attachment
                    </button>
                  )}
                </div>
              ))}
            </div>

            {totalItems > 5 && (
              <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={5} onPageChange={onPageChange} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const SECTIONS = {
  DASHBOARD: "dashboard",
  TRANSACTIONS: "transactions",
  TX_NEW: "tx_new",
  PROFILE: "profile",
};

export default function DashboardPage({ section: propSection }) {
  const { auth, logout } = useAuth();
  const { uid, password } = auth;
  const navigate = useNavigate();
  const { section: paramSection } = useParams();

  const activeSection = propSection || paramSection || SECTIONS.DASHBOARD;
  const setActiveSection = (key) => {
    const path = key === SECTIONS.DASHBOARD ? "/dashboard" : `/${key}`;
    navigate(path, { replace: false });
  };

  const goToSection = (key) => {
    if (key === "tree") { navigate("/tree"); return; }
    if (key === "edit_profile") { navigate("/edit-profile"); return; }
    setActiveSection(key);
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [profile, setProfile] = useState(null);
  const [mlmProfile, setMlmProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const [txTotalItems, setTxTotalItems] = useState(0);
  const [txSearch, setTxSearch] = useState("");
  const [networkCount, setNetworkCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [deactiveCount, setDeactiveCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [partnerId, setPartnerId] = useState(null);
  const [nodeId, setNodeId] = useState("");
  const [rankHistory, setRankHistory] = useState([]);
  const [postIncentives, setPostIncentives] = useState([]);

  const fetchedSections = useRef(new Set());
  const partnerIdRef = useRef(null);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [txLoading, setTxLoading] = useState(false);
  const [txFilter, setTxFilter] = useState("all");
  const [txApprovedTotal, setTxApprovedTotal] = useState(0);
  const [txPendingTotal, setTxPendingTotal] = useState(0);

  // Password reset state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetData, setResetData] = useState({ oldPass: "", newPass: "", confirmPass: "" });
  const [resetLoading, setResetLoading] = useState(false);
  const [isDirectReset, setIsDirectReset] = useState(false);
  const [sponsorCode, setSponsorCode] = useState("");

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetData.newPass !== resetData.confirmPass) {
      toast.error("New passwords do not match!");
      return;
    }
    if (resetData.newPass.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    setResetLoading(true);
    try {
      const success = await changeUserPassword(uid, resetData.oldPass, resetData.newPass);
      if (success) {
        toast.success("Password updated successfully! Please log in again.");
        setShowResetModal(false);
        setResetData({ oldPass: "", newPass: "", confirmPass: "" });
        // After password change, session is invalid in Odoo for the old password.
        // We should force logout.
        setTimeout(() => {
          logout();
          navigate("/login", { replace: true });
        }, 1500);
      } else {
        toast.error("Failed to update password. Check your current password.");
      }
    } catch (err) {
      toast.error(err.message || "Failed to update password.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleDirectReset = async (e) => {
    e.preventDefault();
    if (resetData.newPass !== resetData.confirmPass) {
      toast.error("New passwords do not match!");
      return;
    }
    if (resetData.newPass.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    setResetLoading(true);
    try {
      const { resetPasswordDirect } = await import("../script/rpc");
      await resetPasswordDirect(uid, resetData.newPass);
      toast.success("Password reset successfully!");
      setShowResetModal(false);
      setIsDirectReset(false);
      setResetData({ oldPass: "", newPass: "", confirmPass: "" });
    } catch (err) {
      toast.error(err.message || "Reset failed.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleForgotClick = () => {
    setIsDirectReset(true);
  };


  useEffect(() => {
    let alive = true;
    const isPaginatedSection = activeSection === SECTIONS.TRANSACTIONS;
    const loadKey = `${activeSection}_${partnerId || "init"}_${isPaginatedSection ? txCurrentPage + "_" + txFilter : ""}`;
    if (fetchedSections.current.has(loadKey)) return;

    (async () => {
      let pid = partnerId;
      if (!pid) {
        try {
          const info = await fetchCurrentUserInfo(uid, password);
          if (!info?.partner_id) throw new Error("Could not load user info.");
          pid = info.partner_id[0];
          partnerIdRef.current = pid;
          if (alive) { setUserInfo(info); setPartnerId(pid); }
        } catch (e) {
          if (alive) { setError(e.message); setLoading(false); }
          return;
        }
      }

      try {
        if (activeSection === SECTIONS.DASHBOARD) {
          if (!profile) setSectionLoading(true);
          const [fullProf, admin] = await Promise.all([fetchFullProfile(uid, password, pid), isAdminUser(uid, password)]);
          if (!alive) return;
          const treepath = fullProf?.mlm_treepath;
          const [netCount, statusData, history, incentives, txns] = await Promise.all([
            treepath ? countUserNetwork(uid, password, treepath, pid) : Promise.resolve(0),
            fetchDirectStatusCounts(uid, password, pid),
            fetchRankHistory(uid, password, pid),
            fetchPostIncentives(uid, password),
            fetchTransactions(uid, password, admin ? null : pid),
          ]);
          if (!alive) return;
          setProfile(fullProf); setMlmProfile(fullProf);
          setNetworkCount(netCount);
          setActiveCount(statusData.active); setDeactiveCount(statusData.deactive);
          setRankHistory(history); setPostIncentives(incentives); setTransactions(txns);
          setIsAdmin(admin); setNodeId(fullProf?.mlm_sequence || "");
          fetchedSections.current.add(loadKey);
          fetchedSections.current.add(`${SECTIONS.PROFILE}_${pid}`);

        } else if (activeSection === SECTIONS.TRANSACTIONS || activeSection === SECTIONS.TX_NEW) {
          const needsProfile = !profile;
          if (needsProfile || transactions.length === 0) setSectionLoading(true);
          setTxLoading(true);

          let currentIsAdmin = isAdmin;
          if (needsProfile) {
            currentIsAdmin = await isAdminUser(auth.uid, auth.password);
            setIsAdmin(currentIsAdmin);
          }

          const offset = (txCurrentPage - 1) * 5;
          const txPid = currentIsAdmin ? null : pid;
          const calls = [
            fetchTransactions(uid, password, txPid, { offset, limit: 5, filter: txFilter }),
            countTransactions(uid, password, txPid, { filter: txFilter }),
            countTransactions(uid, password, txPid, { filter: "approved" }),
            countTransactions(uid, password, txPid, { filter: "pending" }),
          ];
          if (needsProfile) calls.push(fetchFullProfile(uid, password, pid));
          const results = await Promise.all(calls);
          if (!alive) return;
          setTransactions(results[0]); setTxTotalItems(results[1] || 0);
          setTxApprovedTotal(results[2] || 0); setTxPendingTotal(results[3] || 0);
          if (needsProfile) {
            setProfile(results[4]); setMlmProfile(results[4]);
            setNodeId(results[4]?.mlm_sequence || "");
          }
          setTxLoading(false);

        } else if (activeSection === SECTIONS.PROFILE) {
          if (!profile) setSectionLoading(true);
          const [fullProf, admin] = await Promise.all([fetchFullProfile(uid, password, pid), isAdminUser(uid, password)]);
          if (!alive) return;
          const treepath = fullProf?.mlm_treepath;
          const [netCount, statusData] = await Promise.all([
            treepath ? countUserNetwork(uid, password, treepath, pid) : Promise.resolve(0),
            fetchDirectStatusCounts(uid, password, pid),
          ]);
          if (!alive) return;
          setProfile(fullProf); setMlmProfile(fullProf);
          setNetworkCount(netCount); setActiveCount(statusData.active); setDeactiveCount(statusData.deactive);
          setIsAdmin(admin); setNodeId(fullProf?.mlm_sequence || "");
          fetchedSections.current.add(loadKey);
        }
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) { setSectionLoading(false); setLoading(false); }
      }
    })();

    return () => { alive = false; };
  }, [uid, password, activeSection, partnerId, txCurrentPage, txFilter]);

  // Search debounce
  useEffect(() => {
    if (activeSection !== SECTIONS.TRANSACTIONS) return;
    const timer = setTimeout(() => {
      const pid = partnerIdRef.current;
      if (!pid) return;
      (async () => {
        setTxLoading(true);
        try {
          const offset = (txCurrentPage - 1) * 5;
          const [txns, count] = await Promise.all([
            fetchTransactions(uid, password, pid, { offset, limit: 5 }),
            countTransactions(uid, password, pid),
          ]);
          setTransactions(txns); setTxTotalItems(count);
        } catch (e) { console.error(e); }
        finally { setTxLoading(false); }
      })();
    }, 400);
    return () => clearTimeout(timer);
  }, [txSearch]);

  const refreshTransactions = async () => {
    const pid = partnerIdRef.current;
    if (!pid) return;
    try { const txns = await fetchTransactions(uid, password, pid); setTransactions(txns); } catch (_) { }
  };

  const name = userInfo?.name || "User";
  const email = userInfo?.email || profile?.email || "—";
  const nid = nodeId || mlmProfile?.mlm_sequence || "—";
  const totalAmt = mlmProfile?.total_amount;
  const totalCommission = mlmProfile?.total_commission;
  const childAmt = mlmProfile?.child_total_amount;
  const directKids = mlmProfile?.total_direct_child ?? 0;
  const phone = profile?.phone || "—";
  const city = profile?.city || "—";
  const street = profile?.street || "—";
  const country = profile?.country_id?.[1] || "—";
  const state = profile?.state_id?.[1] || "—";
  const sponsor = profile?.parent_id?.[1] || "—";
  const partnerName = userInfo?.partner_id?.[1] || name;
  const userStatus = mlmProfile?.status || "deactive";

  const paymentType = profile?.payment_type || null;
  const bankAccName = profile?.bank_account_name || "—";
  const bankAccNum = profile?.bank_account_number || "—";
  const bankAccType = profile?.bank_account_type || null;
  const bankIfsc = profile?.bank_ifsc_code || "—";
  const bankBranch = profile?.bank_branch || "—";
  const upiCode = profile?.upi_reference_code || "—";
  const gatewayRef = profile?.payment_gateway_reference || "—";
  const PAYMENT_TYPE_LABELS = { bank: "Bank Transfer", upi: "UPI", gateway: "Payment Gateway", cash: "Cash" };
  const ACCOUNT_TYPE_LABELS = { saving: "Savings", current: "Current", salary: "Salary" };

  const txTotal = transactions.reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <main className="dp-main" style={{ position: "relative" }}>
        {(loading || sectionLoading) && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 100,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            background: "rgba(248,250,252,0.8)", backdropFilter: "blur(4px)", borderRadius: 20,
          }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", borderWidth: "4px", borderStyle: "solid", borderColor: "#e2e8f0", borderTopColor: "#7c3aed", animation: "dp-spin 0.8s linear infinite", marginBottom: 20 }} />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#64748b" }}>Loading …</p>
          </div>
        )}

        {/* ══ DASHBOARD ══ */}
        {!sectionLoading && activeSection === SECTIONS.DASHBOARD && (
          <>
            <div className="dp-page-header">
              <div>
                <h1 className="dp-page-title">
                  Hello, <span className="dp-highlight">{name.split(" ")[0]}</span> 👋
                </h1>
                <p className="dp-page-sub">
                  {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                  {" "}|{" "}
                  {new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
                </p>
              </div>
              <div className="dp-status-pill" style={userStatus !== "active" ? { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626" } : {}}>
                <span className="dp-status-dot" style={userStatus !== "active" ? { background: "#ef4444", boxShadow: "0 0 0 2px rgba(239,68,68,0.25)" } : {}} />
                {userStatus === "active" ? "Active" : "Deactive"}
              </div>
            </div>

            {error && (
              <div className="dp-error-bar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                {error}
              </div>
            )}

            {/* Stats */}
            <div className="dp-stats-grid">
              <div onClick={() => { setActiveSection(SECTIONS.TRANSACTIONS); setTxFilter("all"); }} style={{ cursor: "pointer" }}>
                <StatCard label="Transactions" value={fmt(txTotal)} sub={`${transactions.length} total`} icon={Icons.wallet} color="amber" delay={120} />
              </div>
              <a href="/team" style={{ textDecoration: "none" }}>
                <StatCard label="Team Amount" value={fmt(childAmt)} icon={Icons.globe} color="blue" delay={60} />
              </a>
              <a href="/commissions" style={{ textDecoration: "none" }}>
                <StatCard label="Total Commission" value={fmt(totalCommission)} icon={Icons.rupee} color="emerald" delay={0} />
              </a>
              <div onClick={() => navigate("/team")} style={{ cursor: "pointer" }}>
                <StatCard
                  label="Team Size"
                  value={fmtNum(directKids)}
                  sub={
                    <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                      <span style={{ color: "#10b981", fontWeight: 600 }}>{activeCount} Active</span>
                      <span style={{ color: "#94a3b8" }}>•</span>
                      <span style={{ color: "#ef4444", fontWeight: 600 }}>{deactiveCount} Deactive</span>
                    </div>
                  }
                  icon={Icons.users}
                  color="rose"
                  delay={180}
                />
              </div>
            </div>
            {/* Mid row */}
            <div className="dp-mid-grid">
              {/* Profile card */}
              <div className="dp-card dp-card--anim" style={{ animationDelay: "100ms" }}>
                <div className="dp-card-head">
                  <div className="dp-card-title-group">
                    <div className="dp-card-icon-wrap dp-card-icon-wrap--violet">{Icons.user}</div>
                    <h2 className="dp-card-title">Profile</h2>
                  </div>
                </div>
                <div className="dp-profile-grid">
                  {[
                    ["Full Name", name], ["Email", email], ["Phone", phone],
                    ["LTX", (mlmProfile?.ltx || "").replace(/\s+/g, "") || "—"],
                    ["City", city], ["Status", userStatus === "active" ? "Active" : "Deactive"],
                    ["Country", country], ["State", state], ["Street", street], ["ZIP", profile?.zip || "—"],
                  ].map(([label, value]) => (
                    <div key={label} className="dp-profile-item">
                      <span className="dp-profile-label">{label}</span>
                      <span className="dp-profile-value">{value}</span>
                    </div>
                  ))}
                  {(!profile?.parent_id || profile?.parent_id?.length === 0) && getCookie("mlm_referral_code") && (
                    <div className="dp-tag" style={{ background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd" }}>
                      <span>Ref Tracker: {getCookie("mlm_referral_code")}</span>
                    </div>
                  )}
                </div>
                {/* Referral Link */}
                <div style={{ marginTop: 20, padding: "14px", background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 15 }}>🚀</span> Share your referral link
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 0, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {`${window.location.origin}/signup?referral=${nid}`}
                    </div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/signup?referral=${nid}`); toast.success("Referral link copied!"); }}
                      style={{ padding: "8px 12px", borderRadius: 8, background: "#7c3aed", color: "#fff", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => { if (navigator.share) { navigator.share({ title: "Join XAMTA", url: `${window.location.origin}/signup?referral=${nid}` }); } }}
                      style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #7c3aed", color: "#7c3aed", background: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      Share
                    </button>
                  </div>
                </div>
              </div>

              {/* Level Open Status */}
              <div className="dp-card dp-card--anim" style={{ animationDelay: "160ms" }}>
                <div className="dp-card-head" style={{ marginBottom: 12 }}>
                  <div className="dp-card-title-group">
                    <div className="dp-card-icon-wrap dp-card-icon-wrap--blue">📋</div>
                    <h2 className="dp-card-title">Level Open Status</h2>
                  </div>
                </div>
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 280 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
                        <th style={{ padding: "8px 4px" }}>Level</th>
                        <th style={{ padding: "8px 4px" }}>Rank</th>
                        <th style={{ padding: "8px 4px" }}>Budget</th>
                        <th style={{ padding: "8px 4px", textAlign: "right" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {postIncentives.filter((inc) => inc.serial_number > 0).map((inc) => {
                        const userRankId = profile?.post_id ? profile.post_id[0] : null;
                        const userRankSerial = postIncentives.find((p) => p.id === userRankId)?.serial_number || 0;
                        const isOpen = inc.serial_number <= userRankSerial;
                        return (
                          <tr key={inc.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                            <td style={{ padding: "10px 4px", fontSize: 12, fontWeight: 700, color: "#1e293b" }}>#{inc.serial_number}</td>
                            <td style={{ padding: "10px 4px", fontSize: 12, fontWeight: 600, color: "#475569" }}>{inc.name}</td>
                            <td style={{ padding: "10px 4px", fontSize: 12, fontWeight: 700, color: isOpen ? "#10b981" : "#241e1fff" }}>
                              ₹{Number(inc.min_budget || 0).toLocaleString("en-IN")}
                            </td>
                            <td style={{ padding: "10px 4px", textAlign: "right" }}>
                              <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 20, background: isOpen ? "#ecfdf5" : "#fff1f2", color: isOpen ? "#10b981" : "#f43f5e", border: `1px solid ${isOpen ? "#bbf7d0" : "#fecaca"}` }}>
                                {isOpen ? "OPEN" : "LOCKED"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ══ TRANSACTIONS LIST ══ */}
        {!sectionLoading && activeSection === SECTIONS.TRANSACTIONS && (
          <>
            <div className="dp-page-header">
              <div>
                <h1 className="dp-page-title">Transactions</h1>
                <p className="dp-page-sub">Manage and track all your financial transactions</p>
              </div>
              <div className="dp-status-pill">
                <span className="dp-status-dot" /> {transactions.length} Records
              </div>
            </div>

            {/* Summary mini stats */}
            <div className="dp-tx-summary-grid">
              <div onClick={() => setTxFilter("all")} style={{ cursor: "pointer" }}>
                <MiniStatCard label="Total" value={fmt(txTotal)} color="amber" active={txFilter === "all"} />
              </div>
              <div onClick={() => setTxFilter("approved")} style={{ cursor: "pointer" }}>
                <MiniStatCard label="Approved" value={txApprovedTotal} color="emerald" isCount active={txFilter === "approved"} />
              </div>
              <div onClick={() => setTxFilter("pending")} style={{ cursor: "pointer" }}>
                <MiniStatCard label="Pending" value={txPendingTotal} color="rose" isCount active={txFilter === "pending"} />
              </div>
            </div>

            <TransactionList
              transactions={transactions.filter((t) => {
                const s = txSearch.toLowerCase();
                if (!s) return true;
                return (
                  (t.ltx || "").toLowerCase().includes(s) ||
                  (t.meta || "").toLowerCase().includes(s) ||
                  (t.amount || "").toString().includes(s)
                );
              })}
              totalItems={txTotalItems}
              currentPage={txCurrentPage}
              onPageChange={(page) => setTxCurrentPage(page)}
              partnerName={partnerName}
              onNewClick={() => setActiveSection(SECTIONS.TX_NEW)}
              search={txSearch}
              onSearchChange={(val) => { setTxSearch(val); setTxCurrentPage(1); }}
              loading={txLoading}
              isAdmin={isAdmin}
            />
          </>
        )}

        {/* ══ NEW TRANSACTION ══ */}
        {!sectionLoading && activeSection === SECTIONS.TX_NEW && (
          <>
            <div className="dp-page-header">
              <div>
                <h1 className="dp-page-title">New Transaction</h1>
                <p className="dp-page-sub">Record a new transaction</p>
              </div>
            </div>
            <TransactionForm
              uid={uid} password={password} partnerId={partnerId}
              partnerName={partnerName} nodeId={nid} ltx={mlmProfile?.ltx}
              onSuccess={() => { refreshTransactions(); setActiveSection(SECTIONS.TRANSACTIONS); }}
              onDiscard={() => setActiveSection(SECTIONS.TRANSACTIONS)}
            />
          </>
        )}

        {/* ══ PROFILE ══ */}
        {!sectionLoading && activeSection === SECTIONS.PROFILE && (
          <>
            <div className="dp-page-header">
              <div><h1 className="dp-page-title">My Profile</h1></div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => { setShowResetModal(true); setIsDirectReset(true); }}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                  Forgot Password?
                </button>
                <button
                  onClick={() => navigate("/edit-profile")}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "none", background: "#534AB7", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
                >
                  {Icons.edit} Edit Profile
                </button>
              </div>
            </div>

            {/* Stats strip */}
            <div className="dp-prof-stats-strip">
              {[
                { label: "Total Commission", value: fmt(totalAmt), color: "#534AB7" },
                { label: "Team Total", value: fmt(childAmt), color: "#185FA5" },
                { label: "Total Team", value: fmtNum(networkCount), color: "#3B6D11" },
                { label: "Team Size", value: fmtNum(directKids), color: "#854F0B" },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: "#f8fafc", borderRadius: 8, padding: "14px 16px" }}>
                  <div style={{ fontSize: 18, fontWeight: 500, color }}>{value}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Detail sections */}
            <div className="dp-prof-detail-grid">
              {/* Personal */}
              <ProfileSection title="👤 Personal Information">
                {[
                  { label: "Full Name", value: name },
                  { label: "Email", value: email },
                  { label: "Phone", value: phone },
                  { label: "LTX", value: (mlmProfile?.ltx || "").replace(/\s+/g, "") || "—", accent: true },
                  { label: "Role", value: isAdmin ? "Administrator" : "Member", accent: isAdmin },
                ].map((row) => <ProfileRow key={row.label} {...row} />)}
              </ProfileSection>

              {/* Address */}
              <ProfileSection title="📍 Address">
                {[
                  { label: "Street", value: (profile?.street || "—") + (profile?.street2 ? `, ${profile.street2}` : "") },
                  { label: "City", value: city },
                  { label: "Postal Code", value: profile?.zip || "—" },
                  { label: "State", value: profile?.state_id?.[1] || "—" },
                  { label: "Country", value: country },
                ].map((row) => <ProfileRow key={row.label} {...row} />)}
              </ProfileSection>

              {/* MLM Details */}
              <ProfileSection title="🌐 MLM Details">
                {[
                  { label: "Node ID", value: nid, accent: true },
                  { label: "Sponsor / Upline", value: sponsor },
                  { label: "Direct Referrals", value: fmtNum(directKids) },
                  { label: "Total Network", value: fmtNum(networkCount) },
                ].map((row) => <ProfileRow key={row.label} {...row} />)}
              </ProfileSection>

              {/* Payment */}
              <ProfileSection title="💳 Bank Details">
                {!paymentType ? (
                  <div style={{ padding: "18px 20px", display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 22 }}>💳</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8" }}>No payment method set yet</div>
                      <button onClick={() => navigate("/edit-profile")} style={{ marginTop: 4, background: "none", border: "none", color: "#6366f1", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
                        Add payment method →
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <ProfileRow label="Payment Type" value={PAYMENT_TYPE_LABELS[paymentType] || paymentType} accent />
                    {paymentType === "bank" && (<>
                      <ProfileRow label="Account Holder" value={bankAccName} />
                      <ProfileRow label="Account Number" value={bankAccNum} mono />
                      <ProfileRow label="Account Type" value={bankAccType ? ACCOUNT_TYPE_LABELS[bankAccType] : "—"} />
                      <ProfileRow label="IFSC Code" value={bankIfsc} mono />
                      <ProfileRow label="Branch" value={bankBranch} />
                    </>)}
                    {paymentType === "upi" && <ProfileRow label="UPI ID / Reference" value={upiCode} mono />}
                    {paymentType === "gateway" && <ProfileRow label="Gateway Reference" value={gatewayRef} mono />}
                    {paymentType === "cash" && <ProfileRow label="Note" value="Cash — no additional details required" />}
                  </>
                )}
              </ProfileSection>
            </div>
          </>
        )}
        {/* ══ RESET PASSWORD MODAL ══ */}
        {showResetModal && (
          <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", padding: 20 }}>
            <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 400, padding: 32, boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0 }}>
                  {isDirectReset ? "Direct Password Reset" : "Reset Password"}
                </h2>
                <button onClick={() => { setShowResetModal(false); setIsDirectReset(false); }} style={{ color: "#94a3b8" }}>{Icons.close}</button>
              </div>

              <form onSubmit={isDirectReset ? handleDirectReset : handleResetPassword}>
                {!isDirectReset && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}>Current Password</label>
                      <button
                        type="button"
                        onClick={handleForgotClick}
                        style={{ fontSize: 11, fontWeight: 600, color: "#7c3aed", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                      >
                        Forgot?
                      </button>
                    </div>
                    <input
                      type="password" required
                      className="dp-form-input"
                      value={resetData.oldPass}
                      onChange={(e) => setResetData({ ...resetData, oldPass: e.target.value })}
                      placeholder="Enter current password"
                    />
                  </div>
                )}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>New Password</label>
                  <input
                    type="password" required
                    className="dp-form-input"
                    value={resetData.newPass}
                    onChange={(e) => setResetData({ ...resetData, newPass: e.target.value })}
                    placeholder="Min 6 characters"
                  />
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 8 }}>Confirm New Password</label>
                  <input
                    type="password" required
                    className="dp-form-input"
                    value={resetData.confirmPass}
                    onChange={(e) => setResetData({ ...resetData, confirmPass: e.target.value })}
                    placeholder="Repeat new password"
                  />
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    type="button" onClick={() => { setShowResetModal(false); setIsDirectReset(false); }}
                    style={{ flex: 1, padding: "12px", borderRadius: 12, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 14, fontWeight: 700 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit" disabled={resetLoading}
                    style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#7c3aed,#4f46e5)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: resetLoading ? "not-allowed" : "pointer", opacity: resetLoading ? 0.8 : 1 }}
                  >
                    {resetLoading ? "Updating…" : isDirectReset ? "Direct Reset" : "Update Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────────────────────────── */
function ProfileSection({ title, children }) {
  return (
    <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "11px 16px", background: "#f8fafc", borderBottom: "0.5px solid #e2e8f0", fontSize: 12, fontWeight: 500, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function ProfileRow({ label, value, accent, mono }) {
  return (
    <div
      style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "10px 20px", borderBottom: "1px solid #f8fafc", gap: 12 }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#fafbff")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "")}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap", flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: accent ? 700 : 500, color: accent ? "#6366f1" : "#1e293b", textAlign: "right", wordBreak: "break-word", fontFamily: mono ? "'Courier New', monospace" : "inherit" }}>
        {value}
      </span>
    </div>
  );
}

function StatCard({ label, value, sub, icon, color, delay }) {
  const c = COLOR_MAP[color];
  return (
    <div className="dp-stat-card dp-card--anim" style={{ animationDelay: `${delay}ms` }}>
      <div className="dp-stat-top">
        <div className="dp-stat-icon-wrap" style={{ background: c.bg }}>
          <span style={{ color: c.icon }}>{React.cloneElement(icon, { size: 20 })}</span>
        </div>
        <span className="dp-stat-sub">{sub}</span>
      </div>
      <div className="dp-stat-value">{value}</div>
      <div className="dp-stat-label">{label}</div>
      <div className="dp-stat-bar-track">
        <div className="dp-stat-bar-fill dp-stat-bar--animate" style={{ background: c.bar }} />
      </div>
    </div>
  );
}

function MiniStatCard({ label, value, color, isCount, active }) {
  const c = COLOR_MAP[color];
  return (
    <div style={{ background: "#fff", border: `1.5px solid ${active ? c.bar : "#e2e8f0"}`, borderRadius: 12, padding: "12px 14px", boxShadow: active ? `0 0 0 3px ${c.bar}22` : "0 1px 3px rgba(15,23,42,0.04)", transition: "all 0.15s" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: c.text, letterSpacing: "-0.5px" }}>{isCount ? value : value}</div>
      <div style={{ height: 3, background: "#f1f5f9", borderRadius: 99, marginTop: 8, overflow: "hidden" }}>
        <div style={{ height: "100%", width: "65%", background: c.bar, borderRadius: 99 }} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   GLOBAL CSS  — full mobile-first responsive rewrite
───────────────────────────────────────────────────────────── */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
button { cursor: pointer; border: none; background: none; font-family: inherit; }

@keyframes dp-spin  { to { transform: rotate(360deg); } }
@keyframes dp-up    { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
@keyframes dp-bar   { from { width:0; } to { width: 68%; } }

.dp-root {
  display: flex; height: 100vh; overflow: hidden;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  background: #f1f5f9; color: #0f172a;
}

/* ═══ SIDEBAR ═══ */
.dp-sidebar {
  width: 256px; flex-shrink: 0;
  background: #fff; border-right: 1px solid #e2e8f0;
  display: flex; flex-direction: column;
  position: sticky; top: 0; height: 100vh;
  overflow-y: auto; z-index: 100;
  transition: transform 0.28s cubic-bezier(0.4,0,0.2,1);
}
.dp-overlay { display: none; }

.dp-brand {
  display: flex; align-items: center; gap: 10px;
  padding: 20px 18px 18px; border-bottom: 1px solid #f1f5f9;
}
.dp-brand-logo {
  width: 34px; height: 34px; border-radius: 10px;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; box-shadow: 0 2px 8px rgba(124,58,237,0.3);
}
.dp-brand-name { font-size: 14px; font-weight: 800; color: #0f172a; letter-spacing: -0.3px; flex: 1; }
.dp-sidebar-close { display: none; color: #94a3b8; padding: 4px; }

.dp-user-card {
  display: flex; align-items: center; gap: 12px;
  margin: 14px 12px; padding: 12px 14px;
  background: linear-gradient(135deg, #f5f3ff, #ede9fe);
  border-radius: 12px; border: 1px solid #ddd6fe;
}
.dp-avatar {
  width: 40px; height: 40px; border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 800; color: #fff; flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(124,58,237,0.25);
}
.dp-user-info { min-width: 0; }
.dp-user-name { font-size: 13px; font-weight: 700; color: #1e1b4b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px; }
.dp-user-meta { font-size: 11px; color: #7c3aed; font-weight: 600; margin-top: 2px; }

.dp-nav { padding: 6px 10px; flex: 1; }
.dp-nav-label { font-size: 10px; font-weight: 700; color: #cbd5e1; text-transform: uppercase; letter-spacing: 0.8px; padding: 8px 8px 6px; }
.dp-nav-item {
  display: flex; align-items: center; gap: 10px;
  width: 100%; padding: 9px 10px; border-radius: 9px;
  color: #64748b; font-size: 14.5px; font-weight: 600;
  text-align: left; transition: background 0.15s, color 0.15s;
  position: relative; margin-bottom: 1px;
  min-height: 44px;
}
.dp-nav-item:hover { background: #f8fafc; color: #0f172a; }
.dp-nav-item--active { background: #f5f3ff !important; color: #7c3aed !important; }
.dp-nav-item--logout { color: #ef4444; }
.dp-nav-item--logout:hover { background: #fef2f2; color: #dc2626; }
.dp-nav-icon { display: flex; align-items: center; flex-shrink: 0; }
.dp-nav-text { flex: 1; }
.dp-nav-dot { width: 6px; height: 6px; border-radius: 50%; background: #7c3aed; }

.dp-sidebar-footer { padding: 0 10px 16px; }
.dp-sidebar-divider { height: 1px; background: #f1f5f9; margin: 6px 0 10px; }
.dp-powered { padding: 8px 10px 0; }
.dp-powered-link { font-size: 12.5px; color: #94a3b8; text-decoration: none; font-weight: 500; transition: color 0.15s; }
.dp-powered-link:hover { color: #7c3aed; }

/* ═══ CONTENT ═══ */
.dp-content { flex: 1; min-width: 0; display: flex; flex-direction: column; height: 100vh; overflow: hidden; }
.dp-mobile-header { display: none; align-items: center; justify-content: space-between; padding: 12px 16px; background: #fff; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; z-index: 90; }
.dp-hamburger { padding: 6px; color: #64748b; border-radius: 8px; transition: background 0.15s; min-height: 44px; min-width: 44px; display: flex; align-items: center; justify-content: center; }
.dp-hamburger:hover { background: #f1f5f9; }
.dp-mobile-brand { display: flex; align-items: center; gap: 8px; }
.dp-mobile-avatar { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg,#7c3aed,#4f46e5); color: #fff; font-size: 11px; font-weight: 800; display: flex; align-items: center; justify-content: center; }

.dp-main { flex: 1; padding: 28px 28px 32px; overflow-y: auto; overflow-x: hidden; max-width: 1280px; width: 100%; margin: 0 auto; }

/* ═══ PAGE HEADER ═══ */
.dp-page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 24px; animation: dp-up 0.4s ease both; flex-wrap: wrap; }
.dp-page-title { font-size: clamp(18px, 2.5vw, 26px); font-weight: 800; color: #0f172a; letter-spacing: -0.6px; line-height: 1.2; }
.dp-highlight { color: #7c3aed; }
.dp-page-sub { font-size: 13px; color: #94a3b8; font-weight: 400; margin-top: 4px; }
.dp-status-pill { display: flex; align-items: center; gap: 6px; padding: 5px 13px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 99px; font-size: 12px; font-weight: 600; color: #15803d; white-space: nowrap; flex-shrink: 0; }
.dp-status-dot { width: 7px; height: 7px; border-radius: 50%; background: #22c55e; box-shadow: 0 0 0 2px rgba(34,197,94,0.25); }

.dp-error-bar { display: flex; align-items: center; gap: 8px; padding: 11px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; color: #dc2626; font-size: 13px; font-weight: 500; margin-bottom: 20px; }

/* ═══ STATS GRID ═══ */
.dp-stats-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; margin-bottom: 20px; }
.dp-stat-card { background: #fff; border-radius: 14px; border: 1px solid #e2e8f0; padding: 18px 20px 16px; box-shadow: 0 1px 3px rgba(15,23,42,0.04); transition: transform 0.2s, box-shadow 0.2s; }
.dp-stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(15,23,42,0.08); }
.dp-stat-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.dp-stat-icon-wrap { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dp-stat-sub { font-size: 10.5px; color: #94a3b8; font-weight: 600; text-align: right; }
.dp-stat-value { font-size: clamp(15px,2vw,22px); font-weight: 800; color: #0f172a; letter-spacing: -0.8px; line-height: 1.1; margin-bottom: 4px; }
.dp-stat-label { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 12px; }
.dp-stat-bar-track { height: 3px; background: #f1f5f9; border-radius: 99px; overflow: hidden; }
.dp-stat-bar-fill { height: 100%; border-radius: 99px; width: 68%; }
.dp-stat-bar--animate { animation: dp-bar 1.2s cubic-bezier(0.22,1,0.36,1) both; animation-delay: 0.5s; }

/* ═══ CARDS ═══ */
.dp-card--anim { animation: dp-up 0.4s ease both; }
.dp-card { background: #fff; border-radius: 14px; border: 1px solid #e2e8f0; padding: 20px 22px; box-shadow: 0 1px 3px rgba(15,23,42,0.04); }
.dp-card-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.dp-card-title-group { display: flex; align-items: center; gap: 10px; }
.dp-card-icon-wrap { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dp-card-icon-wrap--violet { background: #f5f3ff; color: #7c3aed; }
.dp-card-icon-wrap--amber  { background: #fffbeb; color: #d97706; }
.dp-card-icon-wrap--blue   { background: #eff6ff; color: #2563eb; }
.dp-card-title { font-size: 14.5px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; }

/* ═══ MID GRID ═══ */
.dp-mid-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 16px; }

/* ═══ PROFILE GRID ═══ */
.dp-profile-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; margin-bottom: 14px; }
.dp-profile-item { display: flex; flex-direction: column; gap: 3px; }
.dp-profile-label { font-size: 10px; font-weight: 700; color: #cbd5e1; text-transform: uppercase; letter-spacing: 0.6px; }
.dp-profile-value { font-size: 13px; font-weight: 500; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dp-tag { display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 99px; font-size: 11.5px; font-weight: 700; }

/* ═══ FORM GRID ═══ */
.dp-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px 32px; }
.dp-form-label { display: block; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 7px; }
.dp-form-input { width: 100%; padding: 9px 13px; border: 1.5px solid #e2e8f0; border-radius: 9px; font-size: 14px; color: #0f172a; background: #fff; box-sizing: border-box; transition: border 0.15s, box-shadow 0.15s; outline: none; font-family: inherit; }
.dp-form-input:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124,58,237,0.1); }
.dp-form-input:disabled { background: #f8fafc; color: #94a3b8; border-color: #f1f5f9; }
.dp-file-zone { position: relative; border: 2px dashed #e2e8f0; border-radius: 9px; padding: 14px 18px; background: #fafbfc; display: flex; align-items: center; gap: 10px; transition: background 0.2s, border-color 0.2s; cursor: pointer; }
.dp-file-zone--ok { background: #f0fdf4; border-color: #a7f3d0; }

/* ═══ TX SUMMARY GRID ═══ */
.dp-tx-summary-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; margin-bottom: 20px; }

/* ═══ TX TABLE vs CARDS ═══ */
.dp-tx-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.dp-tx-card-list { display: none; }

/* ═══ PROFILE DETAIL GRID ═══ */
.dp-prof-detail-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 12px; margin-bottom: 12px; }
.dp-prof-stats-strip { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 10px; margin-bottom: 12px; }

/* ════════════════════════════════
   RESPONSIVE BREAKPOINTS
════════════════════════════════ */

/* ── Tablet (≤ 1024px) ── */
@media (max-width: 1024px) {
  .dp-stats-grid { grid-template-columns: repeat(2,1fr); }
  .dp-prof-stats-strip { grid-template-columns: repeat(2, minmax(0,1fr)); }
}

/* ── Mobile (≤ 768px) ── */
@media (max-width: 768px) {
  /* Sidebar overlay */
  .dp-overlay { display: block; position: fixed; inset: 0; background: rgba(15,23,42,0.4); z-index: 99; backdrop-filter: blur(2px); }
  .dp-sidebar { position: fixed; top: 0; left: 0; height: 100vh; transform: translateX(-100%); z-index: 200; box-shadow: 4px 0 24px rgba(15,23,42,0.12); }
  .dp-sidebar--open { transform: translateX(0); }
  .dp-sidebar-close { display: flex; align-items: center; justify-content: center; margin-left: auto; border-radius: 6px; transition: background 0.15s; }
  .dp-mobile-header { display: flex; }
  .dp-content { height: 100vh; width: 100%; }

  /* Main padding */
  .dp-main { padding: 16px 14px 60px; }

  /* Stats */
  .dp-stats-grid { grid-template-columns: repeat(2,1fr); gap: 10px; margin-bottom: 14px; }
  .dp-stat-card { padding: 14px 14px 12px; }
  .dp-stat-value { font-size: 16px; }
  .dp-stat-label { font-size: 11px; margin-bottom: 8px; }

  /* Mid grid → single col */
  .dp-mid-grid { grid-template-columns: 1fr; gap: 12px; }

  /* Profile grid → single col */
  .dp-profile-grid { grid-template-columns: 1fr; }

  /* TX summary → 3 col stays but tighter */
  .dp-tx-summary-grid { gap: 8px; margin-bottom: 14px; }

  /* TX table: hide, show cards */
  .dp-tx-table-wrap { display: none; }
  .dp-tx-card-list { display: block; }

  /* Form grid → single col */
  .dp-form-grid { grid-template-columns: 1fr; gap: 14px; }

  /* Profile detail/stats */
  .dp-prof-detail-grid { grid-template-columns: 1fr; }
  .dp-prof-stats-strip { grid-template-columns: repeat(2, minmax(0,1fr)); }

  /* Page header */
  .dp-page-title { font-size: 20px; }
  .dp-page-sub { font-size: 12px; }

  /* Card padding */
  .dp-card { padding: 16px; }
  .dp-card-title { font-size: 14px; }
}

/* ── Small phones (≤ 480px) ── */
@media (max-width: 480px) {
  .dp-main { padding: 12px 10px 60px; }

  .dp-stats-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
  .dp-stat-card { padding: 12px 12px 10px; }
  .dp-stat-icon-wrap { width: 32px; height: 32px; }
  .dp-stat-value { font-size: 14px; }

  .dp-tx-summary-grid { grid-template-columns: repeat(3,1fr); gap: 6px; }

  .dp-prof-stats-strip { grid-template-columns: 1fr 1fr; }

  .dp-page-title { font-size: 18px; }
  .dp-status-pill { font-size: 11px; padding: 4px 10px; }

  .dp-card { padding: 12px; }

  /* Tighter referral link on small screens */
  .dp-referral-row { flex-direction: column; }
}

/* ── Very small (≤ 360px) ── */
@media (max-width: 360px) {
  .dp-stats-grid { grid-template-columns: 1fr 1fr; gap: 6px; }
  .dp-tx-summary-grid { grid-template-columns: 1fr; }
  .dp-stat-value { font-size: 13px; }
}
`;